import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowPathIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  ChevronUpDownIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const StockAdjustmentsTab = () => {
  const navigate = useNavigate();
  const { 
    products, 
    stockItems,
    warehouses,
    fetchAllData,
    deleteStockMovement
  } = useAppData();
  
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [adjustmentTypeFilter, setAdjustmentTypeFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchAllData();
        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };
    
    loadData();
    
    // Refresh data every 2 minutes
    const refreshInterval = setInterval(() => {
      loadData();
    }, 120000);
    
    // Clean up on unmount
    return () => clearInterval(refreshInterval);
  }, [fetchAllData]);

  // Extract stock adjustments from stock items
  const stockAdjustments = useMemo(() => {
    if (!stockItems || !products) return [];
    
    return stockItems
      .filter(item => item.type === 'ajustement')
      .map(adjustment => {
        // Find the associated product
        const product = products.find(p => p.id === adjustment.productId);
        const productName = product ? product.name : adjustment.product || 'Produit inconnu';
        
        // Calculate adjustment details
        let quantityBefore = 0;
        let quantityAfter = 0;
        let adjustmentValue = 0;
        
        if (adjustment.items && adjustment.items.length > 0) {
          const item = adjustment.items[0];
          quantityBefore = parseInt(item.currentStock) || 0;
          quantityAfter = parseInt(item.newStock) || 0;
          adjustmentValue = quantityAfter - quantityBefore;
        } else if (adjustment.beforeQuantity !== undefined && adjustment.afterQuantity !== undefined) {
          quantityBefore = parseInt(adjustment.beforeQuantity);
          quantityAfter = parseInt(adjustment.afterQuantity);
          adjustmentValue = quantityAfter - quantityBefore;
        } else if (product) {
          quantityBefore = parseInt(adjustment.beforeQuantity || 0);
          adjustmentValue = parseInt(adjustment.quantity || 0);
          quantityAfter = quantityBefore + adjustmentValue;
        }
        
        // Get warehouse name if available
        let locationName = adjustment.location || 'Entrepôt Principal';
        
        if (warehouses && warehouses.length > 0 && adjustment.warehouse) {
          const warehouse = warehouses.find(w => String(w.id) === String(adjustment.warehouse));
          if (warehouse) {
            locationName = warehouse.name;
          }
        }
        
        return {
          id: adjustment.id,
          date: adjustment.date || new Date().toISOString().split('T')[0],
          product: productName,
          productId: adjustment.productId,
          quantityBefore,
          quantityAfter,
          adjustment: adjustmentValue,
          reason: adjustment.reason || adjustment.notes || 'Inventaire physique',
          user: adjustment.employeeId || adjustment.user || 'Admin',
          location: locationName,
          warehouseId: adjustment.warehouse || adjustment.location,
          type: adjustmentValue > 0 ? 'increase' : adjustmentValue < 0 ? 'decrease' : 'no_change'
        };
      });
  }, [products, stockItems, warehouses]);

  // Apply sorting
  const sortedAdjustments = useMemo(() => {
    if (!stockAdjustments) return [];
    
    return [...stockAdjustments].sort((a, b) => {
      if (sortConfig.key === 'date') {
        return sortConfig.direction === 'asc' 
          ? new Date(a.date) - new Date(b.date)
          : new Date(b.date) - new Date(a.date);
      } else if (sortConfig.key === 'adjustment') {
        return sortConfig.direction === 'asc'
          ? a.adjustment - b.adjustment
          : b.adjustment - a.adjustment;
      }
      return 0;
    });
  }, [stockAdjustments, sortConfig]);

  // Apply filters
  const filteredAdjustments = useMemo(() => {
    return sortedAdjustments.filter(adjustment => {
      // Match search term
      const matchesSearch = 
        !searchTerm || 
        adjustment.product.toLowerCase().includes(searchTerm.toLowerCase()) || 
        adjustment.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        adjustment.reason.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Match date filter
      const matchesDate = !dateFilter || adjustment.date === dateFilter;
      
      // Match adjustment type
      const matchesType = 
        adjustmentTypeFilter === 'all' || 
        (adjustmentTypeFilter === 'positive' && adjustment.adjustment > 0) ||
        (adjustmentTypeFilter === 'negative' && adjustment.adjustment < 0) ||
        (adjustmentTypeFilter === 'zero' && adjustment.adjustment === 0);
      
      return matchesSearch && matchesDate && matchesType;
    });
  }, [sortedAdjustments, searchTerm, dateFilter, adjustmentTypeFilter]);

  // Handle column sort
  const handleSort = (key) => {
    if (sortConfig.key === key) {
      setSortConfig({
        ...sortConfig,
        direction: sortConfig.direction === 'asc' ? 'desc' : 'asc'
      });
    } else {
      setSortConfig({ key, direction: 'desc' });
    }
  };
  
  // Handle edit adjustment
  const handleEditAdjustment = (adjustmentId) => {
    navigate(`/stock/movement/${adjustmentId}/edit`);
  };
  
  // Handle delete adjustment
  const handleDeleteAdjustment = (adjustmentId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet ajustement ?')) {
      try {
        deleteStockMovement(adjustmentId);
        toast.success('Ajustement supprimé avec succès');
        // No need to refresh, context will handle it
      } catch (error) {
        console.error('Error deleting adjustment:', error);
        toast.error('Erreur lors de la suppression');
      }
    }
  };
  
  // Handle view adjustment details
  const handleViewAdjustment = (adjustmentId) => {
    navigate(`/stock/movement/${adjustmentId}`);
  };
  
  // Create new adjustment
  const handleNewAdjustment = () => {
    navigate('/stock/adjustment');
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Ajustements de stock</h2>
          <p className="text-white/60 mt-1">
            Historique des ajustements manuels d'inventaire
          </p>
        </div>
        <div>
          <button 
            onClick={handleNewAdjustment}
            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-colors flex items-center space-x-2"
          >
            <ArrowPathIcon className="h-5 w-5" />
            <span>Nouvel ajustement</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
          <input
            type="text"
            placeholder="Rechercher un ajustement..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <div className="relative">
          <div className="relative flex-1">
            <select
              value={adjustmentTypeFilter}
              onChange={(e) => setAdjustmentTypeFilter(e.target.value)}
              className="w-full pl-4 pr-10 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
            >
              <option value="all">Tous les types</option>
              <option value="positive">Ajustements positifs</option>
              <option value="negative">Ajustements négatifs</option>
              <option value="zero">Pas de changement</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-white/50">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
        
        <div className="relative">
          <CalendarIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Adjustments Table */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
        <div className="overflow-x-auto">
          {filteredAdjustments.length > 0 ? (
            <table className="w-full divide-y divide-white/10">
              <thead className="bg-white/5">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">ID</th>
                  <th 
                    className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('date')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Date</span>
                      <ChevronUpDownIcon className="h-4 w-4" />
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Produit</th>
                  <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Avant</th>
                  <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Après</th>
                  <th 
                    className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('adjustment')}
                  >
                    <div className="flex items-center justify-center space-x-1">
                      <span>Ajustement</span>
                      <ChevronUpDownIcon className="h-4 w-4" />
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Raison</th>
                  <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {filteredAdjustments.map((adjustment, index) => (
                  <motion.tr
                    key={adjustment.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 * index }}
                    className="hover:bg-white/5 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-white">
                      {adjustment.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      {adjustment.date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      <div className="flex items-center">
                        <div 
                          className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-medium text-sm mr-2"
                        >
                          {adjustment.product.charAt(0)}
                        </div>
                        <span>{adjustment.product}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-white/80">
                      {adjustment.quantityBefore}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-white/80">
                      {adjustment.quantityAfter}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center">
                      <span className={`${
                        adjustment.adjustment > 0 ? 'text-green-400' : 
                        adjustment.adjustment < 0 ? 'text-red-400' : 
                        'text-white/80'
                      } font-medium`}>
                        {adjustment.adjustment > 0 ? `+${adjustment.adjustment}` : adjustment.adjustment}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white/80">
                      {adjustment.reason || 'Inventaire physique'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex justify-center space-x-2">
                        <button 
                          onClick={() => handleViewAdjustment(adjustment.id)}
                          className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors" 
                          title="Voir"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button 
                          onClick={() => handleEditAdjustment(adjustment.id)}
                          className="p-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors" 
                          title="Modifier"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => handleDeleteAdjustment(adjustment.id)}
                          className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors" 
                          title="Supprimer"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-12 text-white/60">
              <ArrowPathIcon className="h-12 w-12 mx-auto text-white/30 mb-4" />
              <p className="text-lg font-medium text-white mb-1">Aucun ajustement trouvé</p>
              <p className="text-white/50 mb-4">
                {searchTerm || dateFilter || adjustmentTypeFilter !== 'all'
                  ? 'Aucun résultat ne correspond aux filtres sélectionnés'
                  : 'Vous n\'avez pas encore effectué d\'ajustements de stock'}
              </p>
              <button
                onClick={handleNewAdjustment}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-colors inline-flex items-center space-x-2"
              >
                <ArrowPathIcon className="h-5 w-5" />
                <span>Nouvel ajustement</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockAdjustmentsTab;